import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ModalProvider } from "@/context/ModalContext";
import { BUILD_TIMESTAMP } from "@/buildInfo";

// Global Build Marker - only shown on user pages, NOT admin pages
// Positioned above bottom nav on mobile, at bottom on desktop
const GlobalBuildFooter = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Don't show on admin routes
  if (isAdminRoute) return null;
  
  return (
    <div 
      className="lg:hidden fixed left-0 right-0 bottom-16 p-1 text-center text-[10px] text-muted-foreground/40 bg-background/80 backdrop-blur-sm z-40 pointer-events-none"
      data-testid="global-build-marker"
    >
      BUILD: {BUILD_TIMESTAMP}
    </div>
  );
};

// Log build timestamp on initial load
if (typeof window !== 'undefined' && !window.__EXIT88_BUILD_LOGGED__) {
  window.__EXIT88_BUILD_LOGGED__ = true;
  console.log(`%cBUILD: ${BUILD_TIMESTAMP}`, 'color: #00C8C8; font-weight: bold;');
}

// User Pages
import UserEventList from "@/pages/user/UserEventList";
import UserEventDetails from "@/pages/user/UserEventDetails";
import UserTicketSelection from "@/pages/user/UserTicketSelection";
import UserMyTickets from "@/pages/user/UserMyTickets";
import UserTicketQR from "@/pages/user/UserTicketQR";
import UserLogin from "@/pages/user/UserLogin";
import UserRegister from "@/pages/user/UserRegister";
import UserLivestreamView from "@/pages/user/UserLivestreamView";
import UserLivestreamJoin from "@/pages/user/UserLivestreamJoin";
import LivestreamView from "@/pages/user/LivestreamView";
import UserPaymentLoading from "@/pages/user/UserPaymentLoading";
import UserPaymentSuccess from "@/pages/user/UserPaymentSuccess";
import UserPaymentError from "@/pages/user/UserPaymentError";
import UserTipArtist from "@/pages/user/UserTipArtist";
import UserWishlist from "@/pages/user/UserWishlist";
import UserSettings from "@/pages/user/UserSettings";
import UserArtistsList from "@/pages/user/UserArtistsList";
import UserArtistDetails from "@/pages/user/UserArtistDetails";
import UserEventRecap from "@/pages/user/UserEventRecap";

// Admin Pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminSignup from "@/pages/admin/AdminSignup";
import AdminEventDashboard from "@/pages/admin/AdminEventDashboard";
import AdminEventList from "@/pages/admin/AdminEventList";
import AdminDraftList from "@/pages/admin/AdminDraftList";
import AdminEditEventInfo from "@/pages/admin/AdminEditEventInfo";
import AdminDeleteModal from "@/pages/admin/AdminDeleteModal";
import AdminDeleteConfirmed from "@/pages/admin/AdminDeleteConfirmed";
import AdminCreateEventBasics from "@/pages/admin/AdminCreateEventBasics";
import AdminCreateEventLocation from "@/pages/admin/AdminCreateEventLocation";
import AdminCreateEventMedia from "@/pages/admin/AdminCreateEventMedia";
import AdminCreateEventTickets from "@/pages/admin/AdminCreateEventTickets";
import AdminEventRevenue from "@/pages/admin/AdminEventRevenue";
import AdminStreamMonitor from "@/pages/admin/AdminStreamMonitor";
import AdminCheckinScanner from "@/pages/admin/AdminCheckinScanner";
import AdminCustomerDirectory from "@/pages/admin/AdminCustomerDirectory";
import AdminCampaignCreateInfo from "@/pages/admin/AdminCampaignCreateInfo";
import AdminCampaignTargeting from "@/pages/admin/AdminCampaignTargeting";
import AdminCampaignMetrics from "@/pages/admin/AdminCampaignMetrics";
import AdminCampaignsDashboard from "@/pages/admin/AdminCampaignsDashboard";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminArtistsList from "@/pages/admin/AdminArtistsList";
import AdminAddArtist from "@/pages/admin/AdminAddArtist";
import AdminEventAnalytics from "@/pages/admin/AdminEventAnalytics";
import AdminStreamConfig from "@/pages/admin/AdminStreamConfig";

// Deep link wrapper for event details modal
function EventDetailsDeepLink() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  React.useEffect(() => {
    // Redirect to home page where modal will open
    navigate('/', { state: { openEventId: eventId } });
  }, [eventId, navigate]);
  
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ModalProvider>
          <BrowserRouter>
            <Routes>
              {/* ==================== */}
              {/* PUBLIC / USER ROUTES */}
              {/* ==================== */}
              
              {/* Event List (Home) */}
              <Route path="/" element={<UserEventList />} />
              <Route path="/events" element={<UserEventList />} />
              
              {/* Event Details - renders same as home with modal */}
              <Route path="/events/:eventId" element={<UserEventDetails />} />
              
              {/* Event Recap (for past events) */}
              <Route path="/events/:eventId/recap" element={<UserEventRecap />} />
              
              {/* Authentication */}
              <Route path="/login" element={<UserLogin />} />
              <Route path="/register" element={<UserRegister />} />
              
              {/* Ticket Flow */}
              <Route path="/events/:eventId/tickets" element={<UserTicketSelection />} />
              
              {/* My Tickets (master-detail) */}
              <Route path="/me/tickets" element={<UserMyTickets />} />
              <Route path="/me/tickets/:ticketId" element={<UserMyTickets />} />
              <Route path="/me/tickets/:ticketId/qr" element={<UserTicketQR />} />
              
              {/* Wishlist */}
              <Route path="/me/wishlist" element={<UserWishlist />} />
              
              {/* Settings */}
              <Route path="/me/settings" element={<UserSettings />} />
              
              {/* Artists */}
              <Route path="/artists" element={<UserArtistsList />} />
              <Route path="/artists/:artistId" element={<UserArtistDetails />} />
              
              {/* Livestream */}
              <Route path="/events/:eventId/live" element={<LivestreamView />} />
              <Route path="/events/:eventId/watch" element={<UserLivestreamView />} />
              <Route path="/events/:eventId/join" element={<UserLivestreamJoin />} />
              
              {/* Payment */}
              <Route path="/payment/processing" element={<UserPaymentLoading />} />
              <Route path="/payment/success" element={<UserPaymentSuccess />} />
              <Route path="/payment/error" element={<UserPaymentError />} />
              
              {/* Support */}
              <Route path="/support/tip" element={<UserTipArtist />} />
              
              {/* ==================== */}
              {/* ADMIN ROUTES */}
              {/* ==================== */}
              
              {/* Admin Authentication */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/signup" element={<AdminSignup />} />
              
              {/* Admin Dashboard & Events - /admin redirects to /admin/events */}
              <Route path="/admin" element={<Navigate to="/admin/events" replace />} />
              <Route path="/admin/events" element={<AdminEventList />} />
              <Route path="/admin/events/drafts" element={<AdminDraftList />} />
              
              {/* Admin Event Overview (sub-page) */}
              <Route path="/admin/events/:eventId/overview" element={<AdminEventDashboard />} />
              
              {/* Admin Event Details & Edit */}
              <Route path="/admin/events/:eventId" element={<AdminEditEventInfo />} />
              
              {/* Admin Event Delete */}
              <Route path="/admin/events/:eventId/delete" element={<AdminDeleteModal />} />
              <Route path="/admin/events/:eventId/delete/confirmed" element={<AdminDeleteConfirmed />} />
              
              {/* Admin Event Creation (Multi-step) */}
              <Route path="/admin/events/new/basics" element={<AdminCreateEventBasics />} />
              <Route path="/admin/events/new/location" element={<AdminCreateEventLocation />} />
              <Route path="/admin/events/new/media" element={<AdminCreateEventMedia />} />
              <Route path="/admin/events/new/tickets" element={<AdminCreateEventTickets />} />
              
              {/* Admin Event Operations */}
              <Route path="/admin/events/:eventId/analytics" element={<AdminEventAnalytics />} />
              <Route path="/admin/events/:eventId/revenue" element={<AdminEventRevenue />} />
              <Route path="/admin/events/:eventId/stream/config" element={<AdminStreamConfig />} />
              <Route path="/admin/events/:eventId/stream/monitor" element={<AdminStreamMonitor />} />
              <Route path="/admin/events/:eventId/checkin" element={<AdminCheckinScanner />} />
              
              {/* Admin Artists */}
              <Route path="/admin/artists" element={<AdminArtistsList />} />
              <Route path="/admin/artists/new" element={<AdminAddArtist />} />
              <Route path="/admin/artists/:artistId" element={<AdminAddArtist />} />
              
              {/* Admin Customers */}
              <Route path="/admin/customers" element={<AdminCustomerDirectory />} />
              
              {/* Admin Campaigns */}
              <Route path="/admin/campaigns" element={<AdminCampaignsDashboard />} />
              <Route path="/admin/campaigns/new/info" element={<AdminCampaignCreateInfo />} />
              <Route path="/admin/campaigns/new/targeting" element={<AdminCampaignTargeting />} />
              <Route path="/admin/campaigns/:campaignId/metrics" element={<AdminCampaignMetrics />} />
              
              {/* Admin Settings */}
              <Route path="/admin/settings" element={<AdminSettings />} />
              
              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Global Build Marker - renders on ALL routes */}
            <GlobalBuildFooter />
          </BrowserRouter>
        </ModalProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
